import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Slider,
  Typography
} from '@mui/material';
import Cropper, { Area } from 'react-easy-crop';
import { useCallback, useState, useEffect } from 'react';

const ASPECT = 4 / 5;
const OUT_WIDTH = 800;
const OUT_HEIGHT = 1000;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const CROP_AREA_HEIGHT = 420;

interface ImageCropDialogProps {
  open: boolean;
  file: File;
  onClose: () => void;
  onSave: (croppedFile: File) => void;
}

export const ImageCropDialog = ({ open, file, onClose, onSave }: ImageCropDialogProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (open && file) {
      const url = URL.createObjectURL(file);
      setObjectUrl(url);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    setObjectUrl(null);
  }, [open, file]);

  const onCropComplete = useCallback((_croppedArea: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleSave = useCallback(async () => {
    if (!croppedAreaPixels || !objectUrl) return;

    const img = new Image();
    img.src = objectUrl;

    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Image load failed'));
    });

    const canvas = document.createElement('canvas');
    canvas.width = OUT_WIDTH;
    canvas.height = OUT_HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(
      img,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      OUT_WIDTH,
      OUT_HEIGHT
    );

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const croppedFile = new File([blob], file.name, { type: file.type || 'image/jpeg' });
        onSave(croppedFile);
        onClose();
      },
      file.type || 'image/jpeg',
      0.92
    );
  }, [croppedAreaPixels, objectUrl, file, onSave, onClose]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Adjust image (4:5 ratio)</DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: CROP_AREA_HEIGHT,
            mb: 2,
            bgcolor: 'grey.900',
            borderRadius: 1
          }}
        >
          {objectUrl && (
            <Cropper
              image={objectUrl}
              crop={crop}
              zoom={zoom}
              aspect={ASPECT}
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          Zoom
        </Typography>
        <Slider
          value={zoom}
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step={0.1}
          valueLabelDisplay="auto"
          onChange={(_, value) => setZoom(Array.isArray(value) ? value[0] : value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!croppedAreaPixels}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
